/**
 * The game's art (ADR-037 D6): every asset of art/assets.json at its latest
 * render, and the owner's approval threshold — a batch of art estimated above
 * it waits for the owner on this page. The threshold is the owner's alone;
 * Claude can neither read nor change it. "Make again" asks Claude, in a turn,
 * to remake one asset (the manifest stays the source of truth).
 */
import { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CreativeStudioLink } from '@/components/CreativeStudioLink';
import { formatCredits } from '@/lib/format-credits';
import type { ForgeArtOverview } from '../types';

/** Mirrors the server's FORGE_ART_THRESHOLD_MAX_CREDITS. */
export const FORGE_ART_THRESHOLD_MAX = 100_000;

interface ForgeArtPanelProps {
  /** The forge this page is: FiasWorks Studio opens here with a way back to it. */
  forgeId?: string;
  art: ForgeArtOverview | null;
  onSetThreshold: (credits: number | null) => Promise<unknown>;
  isSavingThreshold: boolean;
  /** Ask Claude to make one asset again; absent while that is not possible. */
  onRemake?: (name: string) => void;
  remakeHint?: string;
}

export function ForgeArtPanel({
  forgeId,
  art,
  onSetThreshold,
  isSavingThreshold,
  onRemake,
  remakeHint,
}: ForgeArtPanelProps) {
  const saved = art?.approvalThresholdCredits ?? null;
  const [draft, setDraft] = useState(saved === null ? '' : String(saved));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraft(saved === null ? '' : String(saved)), [saved]);

  const parsed = draft.trim() === '' ? null : Number(draft);
  const valid =
    parsed === null ||
    (Number.isInteger(parsed) && parsed >= 0 && parsed <= FORGE_ART_THRESHOLD_MAX);
  const changed = parsed !== saved;

  const save = async () => {
    if (!valid || !changed) return;
    setError(null);
    try {
      await onSetThreshold(parsed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The threshold was not saved.');
    }
  };

  const assets = art?.assets ?? [];
  return (
    <div className="space-y-3 rounded-lg border p-3" data-testid="forge-art">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium">Game art</h3>
        <CreativeStudioLink
          className="text-xs"
          {...(forgeId ? { from: { kind: 'forge' as const, id: forgeId } } : {})}
        />
      </div>
      {assets.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Claude Code makes the game&apos;s art from <code>art/assets.json</code>, in the styles and
          characters of your Creative Library. Ask it for art; each picture appears here once it is
          made.
        </p>
      ) : (
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {assets.map((asset) => (
            <li key={asset.name} className="min-w-0 space-y-1" data-testid="forge-art-asset">
              <div className="aspect-square overflow-hidden rounded-md border bg-[repeating-conic-gradient(#e5e7eb_0%_25%,transparent_0%_50%)] bg-[length:12px_12px]">
                {asset.previewUrl && (
                  <img
                    src={asset.previewUrl}
                    alt={asset.description || asset.name}
                    className="h-full w-full object-contain"
                    loading="lazy"
                  />
                )}
              </div>
              <p className="truncate text-xs font-medium" title={asset.file}>
                {asset.name}
              </p>
              <p className="truncate text-[11px] text-muted-foreground">
                {asset.costCredits !== null
                  ? `${formatCredits(asset.costCredits)} credits`
                  : asset.kind}
              </p>
              {onRemake && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-full px-1 text-xs"
                  onClick={() => onRemake(asset.name)}
                  title={`Ask Claude Code to make ${asset.name} again`}
                >
                  <RefreshCw className="mr-1 h-3 w-3" />
                  Make again
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!onRemake && assets.length > 0 && remakeHint && (
        <p className="text-[11px] text-muted-foreground">{remakeHint}</p>
      )}
      <div className="space-y-1.5">
        <label htmlFor="forge-art-threshold" className="block text-xs text-muted-foreground">
          Ask me before making art that costs more than (credits) — empty: always ask
        </label>
        <div className="flex gap-2">
          <Input
            id="forge-art-threshold"
            type="number"
            min={0}
            max={FORGE_ART_THRESHOLD_MAX}
            step={1}
            placeholder="Always ask"
            className="h-8 text-sm"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-invalid={!valid}
          />
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            onClick={() => void save()}
            disabled={!valid || !changed || isSavingThreshold}
          >
            {isSavingThreshold && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            Save
          </Button>
        </div>
        {!valid && (
          <p className="text-xs text-destructive">
            A whole number of credits, up to {FORGE_ART_THRESHOLD_MAX.toLocaleString()}.
          </p>
        )}
        {error && (
          <p className="text-xs text-destructive" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
